import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { enterDataContext } from "../lib/dataContext";
import type { SessionUser } from "./useSession";

/** Includes authorization changes, not volatile tokens or profile check times. */
export function sessionDataKey(user: SessionUser): string {
  return JSON.stringify([user.user_id, user.customer_id, user.active_membership]);
}

/** A new confirmed context owns fresh queries, mutations and mounted page state.
 * Request guards reject late results; obsolete queries keep the discarded client.
 * A server signal that access changed hides the view while the profile is rechecked, without
 * resetting unsaved work or navigating away. Routine rechecks of the same identity stay invisible.
 */
export default function SessionDataBoundary({ checking, children }: { checking: boolean; children: ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  } }));
  useLayoutEffect(() => enterDataContext(client), [client]);
  useEffect(() => () => { client.clear(); }, [client]);
  // Dialogs are often portaled to body; hiding the page alone would expose them.
  useLayoutEffect(() => {
    if (checking) document.body.setAttribute("data-session-checking", "true");
    else document.body.removeAttribute("data-session-checking");
    return () => document.body.removeAttribute("data-session-checking");
  }, [checking]);
  return <QueryClientProvider client={client}>
    {checking ? <p role="status" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-50 text-sm text-slate-600">Zugriff wird geprüft …</p> : null}
    <div style={{ visibility: checking ? "hidden" : undefined }} inert={checking}>{children}</div>
  </QueryClientProvider>;
}
